import { useState, useEffect } from 'react';
import { Loader2, CheckCircle } from 'lucide-react';

interface LoadingStateProps {
  hotelName: string;
  hotelLocation: string;
  hotelStars: number;
  selectedOTAs: string[];
  onComplete: () => void;
}

const OTA_NAMES: Record<string, string> = {
  booking: 'Booking.com',
  expedia: 'Expedia',
  hotels: 'Hotels.com',
  agoda: 'Agoda',
};

const ROOM_IMAGES = [
  'https://images.unsplash.com/photo-1611892440504-42a792e24d32?q=80&w=800',
  'https://images.unsplash.com/photo-1618773928121-c32242e63f39?q=80&w=800',
  'https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?q=80&w=800',
  'https://images.unsplash.com/photo-1595576508898-0ad5c879a061?q=80&w=800',
];

const ROOM_TYPES = [
  'Deluxe King Room',
  'Superior Double Room',
  'Executive Suite',
  'Classic Twin Room',
];

interface FoundResult {
  otaId: string;
  otaName: string;
  roomType: string;
  roomImage: string;
  foundAt: number;
}

export const LoadingState = ({
  hotelName,
  hotelLocation,
  hotelStars,
  selectedOTAs,
  onComplete,
}: LoadingStateProps) => {
  const [currentSeconds, setCurrentSeconds] = useState(0);
  const [foundResults, setFoundResults] = useState<FoundResult[]>([]);
  const [currentOTAIndex, setCurrentOTAIndex] = useState(0);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [otaDurations] = useState<number[]>(() =>
    selectedOTAs.map(() => Math.floor(Math.random() * 6) + 5)
  );

  const totalDuration = otaDurations.reduce((a, b) => a + b, 0);
  const currentOTA = selectedOTAs[currentOTAIndex];

  useEffect(() => {
    const secondsInterval = setInterval(() => {
      setCurrentSeconds((prev) => {
        const newSeconds = prev + 1;
        const elapsedForCurrentOTA =
          newSeconds - otaDurations.slice(0, currentOTAIndex).reduce((a, b) => a + b, 0);

        if (elapsedForCurrentOTA >= otaDurations[currentOTAIndex]) {
          const newResult: FoundResult = {
            otaId: selectedOTAs[currentOTAIndex],
            otaName: OTA_NAMES[selectedOTAs[currentOTAIndex]],
            roomType: ROOM_TYPES[currentOTAIndex % ROOM_TYPES.length],
            roomImage: ROOM_IMAGES[currentOTAIndex % ROOM_IMAGES.length],
            foundAt: newSeconds,
          };
          
          setFoundResults((prev) => [...prev, newResult]);

          if (currentOTAIndex < selectedOTAs.length - 1) {
            setCurrentOTAIndex((prev) => prev + 1);
          }
        }

        if (newSeconds >= totalDuration) {
          clearInterval(secondsInterval);
          setTimeout(onComplete, 1000);
          return totalDuration;
        }

        return newSeconds;
      });
    }, 1000);

    const imageInterval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % ROOM_IMAGES.length);
    }, 2000);

    return () => {
      clearInterval(secondsInterval);
      clearInterval(imageInterval);
    };
  }, [selectedOTAs, totalDuration, currentOTAIndex, otaDurations, onComplete]);

  const progress = (currentSeconds / totalDuration) * 100;

  return (
    <section className="ha-min-h-screen ha-bg-gradient-to-br ha-from-[#0a1f1c] ha-to-[#052e2b] ha-py-8 ha-relative ha-overflow-hidden">
      {/* Background Effects */}
      <div className="ha-absolute ha-inset-0 ha-opacity-10">
        <div className="ha-absolute ha-top-0 ha-left-1/4 ha-w-96 ha-h-96 ha-bg-primary ha-rounded-full ha-blur-[150px] ha-animate-pulse" />
        <div className="ha-absolute ha-bottom-0 ha-right-1/4 ha-w-96 ha-h-96 ha-bg-primary ha-rounded-full ha-blur-[150px] ha-animate-pulse" style={{ animationDelay: '1.5s' }} />
      </div>

      <div className="ha-container ha-mx-auto ha-px-4 ha-relative ha-z-10">
        <div className="ha-max-w-7xl ha-mx-auto">
          {/* NEW LAYOUT: Left results, Right analysis */}
          <div className="ha-flex ha-items-start ha-gap-6">
            
            {/* LEFT SIDE - Plateformes Analysées */}
            <div className="ha-space-y-4">
              <div className="ha-flex ha-items-center ha-gap-3 ha-mb-6">
                <h3 className="ha-text-xl ha-font-bold ha-text-black">Plateformes Analysées</h3>
              </div>

              {/* Results List */}
              <div className="ha-space-y-3">
                {foundResults.map((result, index) => (
                  <div
                    key={result.otaId}
                    className="ha-p-4 ha-rounded-2xl ha-border-2 ha-border-primary ha-bg-gradient-to-br ha-from-primary/10 ha-to-primary/5 ha-backdrop-blur-xl"
                    style={{
                      animation: 'slideInLeft 0.5s ease-out',
                      boxShadow: '0 0 30px rgba(30, 214, 179, 0.3)',
                    }}
                  >
                    <div className="ha-flex ha-items-start ha-gap-3 ha-mb-3">
                      <img
                        src={result.roomImage}
                        alt={result.roomType}
                        className="ha-w-20 ha-h-20 ha-rounded-xl ha-object-cover"
                      />
                      <div className="ha-flex-1">
                        <div className="ha-flex ha-items-center ha-gap-2 ha-mb-1">
                          <span className="ha-text-base ha-font-bold ha-text-primary">
                            {result.otaName}
                          </span>
                        </div>
                        <div className="ha-flex ha-items-center ha-gap-2 ha-text-primary ha-text-sm">
                          <CheckCircle className="ha-w-4 ha-h-4" />
                          <span>Trouvé</span>
                        </div>
                      </div>
                    </div>

                    <div className="ha-border-t ha-border-primary/20 ha-pt-3 ha-space-y-2">
                      <div>
                        <div className="ha-text-xs ha-text-gray-400 ha-mb-1">Chambre trouvée:</div>
                        <div className="ha-text-sm ha-font-semibold ha-text-black ha-flex ha-items-center ha-gap-2">
                          <span>🛏️</span>
                          <span>{result.roomType}</span>
                        </div>
                      </div>
                      <div>
                        <div className="ha-text-xs ha-text-gray-400 ha-mb-1">Hôtel:</div>
                        <div className="ha-text-sm ha-font-semibold ha-text-black">{hotelName}</div>
                        <div className="ha-text-xs ha-text-gray-400 ha-flex ha-items-center ha-gap-2 ha-mt-1">
                          <span>📍 {hotelLocation}</span>
                          <span>•</span>
                          <span>{'⭐'.repeat(hotelStars)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* RIGHT SIDE - Analyse en cours */}
            <div className="ha-space-y-6" style={{ width: '100%' }}>
              {/* Header avec timer circulaire à droite */}
              <div className="ha-p-8 ha-rounded-2xl ha-border-2 ha-border-primary/30 ha-bg-gradient-to-br ha-from-gray-900/80 ha-to-gray-800/60 ha-backdrop-blur-xl" 
                   style={{ boxShadow: '0 0 40px rgba(30, 214, 179, 0.3)'}}>

                <div className="ha-flex ha-items-center ha-justify-between ha-mb-6">
                  <div>
                    <h2 className="ha-text-3xl ha-font-bold ha-text-black ha-mb-2 ha-mr-2" style={{ marginRight: '0.5rem' }}>
                      Analyse en cours
                    </h2>
                    <p className="ha-text-gray-400 ha-text-lg">{hotelName}</p>
                  </div>

                  {/* Circular Timer - Small on right */}
                  <div className="ha-relative ha-w-32 ha-space-x-5 ha-h-32 ha-flex-shrink-0">
                    {/* Glow effect */}
                    <div className="ha-absolute ha-inset-0 ha-rounded-full ha-bg-primary/20 ha-blur-xl ha-animate-pulse" />
                    
                    {/* Circle */}
                    <div className="ha-relative ha-w-full ha-h-full ha-rounded-full ha-flex ha-items-center ha-justify-center"
                         style={{
                           background: 'radial-gradient(circle, rgba(30, 214, 179, 0.2) 0%, rgba(30, 214, 179, 0.05) 70%)',
                           border: '3px solid rgba(30, 214, 179, 0.3)',
                         }}>
                      <div className="ha-text-center">
                        <div className="ha-text-4xl ha-font-black ha-text-primary" 
                             style={{ textShadow: '0 0 20px rgba(30, 214, 179, 0.8)' }}>
                          {currentSeconds}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Current OTA Searching */}
                {currentOTA && foundResults.length < selectedOTAs.length && (
                  <div className="ha-p-5 ha-rounded-xl ha-border-2 ha-border-primary/50 ha-bg-primary/10 ha-mb-6" 
                       style={{ boxShadow: '0 0 30px rgba(30, 214, 179, 0.3)' }}>
                    <div className="ha-flex ha-items-center ha-gap-4">
                      {/* Dynamic rotating image */}
                      <div className="ha-relative ha-w-20 ha-h-20 ha-rounded-xl ha-overflow-hidden ha-flex-shrink-0">
                        {ROOM_IMAGES.map((image, index) => (
                          <img
                            key={image}
                            src={image}
                            alt={`Room ${index + 1}`}
                            className="ha-absolute ha-inset-0 ha-w-full ha-h-full ha-object-cover ha-transition-opacity ha-duration-500"
                            style={{ opacity: index === currentImageIndex ? 1 : 0 }}
                          />
                        ))}
                      </div>

                      <div className="ha-flex-1">
                        <div className="ha-text-xl ha-font-bold ha-text-primary ha-mb-2">
                          {OTA_NAMES[currentOTA]}
                        </div>
                        <div className="ha-flex ha-items-center ha-gap-2 ha-text-primary">
                          <Loader2 className="ha-w-5 ha-h-5 ha-animate-spin" />
                          <span className="ha-text-sm ha-font-semibold">Recherche en cours...</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Progress Bar */}
                <div className="ha-mb-6">
                  <div className="ha-flex ha-justify-between ha-text-sm ha-mb-2">
                    <span className="ha-text-gray-400 ha-font-semibold">Progression globale</span>
                    <span className="ha-text-primary ha-font-bold">
                      {foundResults.length} / {selectedOTAs.length} plateformes
                    </span>
                  </div>
                  <div className="ha-relative ha-w-full ha-h-3 ha-bg-gray-800 ha-rounded-full ha-overflow-hidden ha-border ha-border-primary/30">
                    <div
                      className="ha-h-full ha-bg-gradient-to-r ha-from-primary ha-to-[#0d9488] ha-transition-all ha-duration-500 ha-relative"
                      style={{
                        width: `${(foundResults.length / selectedOTAs.length) * 100}%`,
                        boxShadow: '0 0 20px rgba(30, 214, 179, 0.8)',
                      }}
                    >
                      <div className="ha-absolute ha-inset-0 ha-bg-white/20 ha-animate-pulse" />
                    </div>
                  </div>
                </div>

                {/* OTA Status Grid */}
                <div>
                  <h4 className="ha-text-sm ha-font-bold ha-mb-3 ha-text-gray-400 ha-uppercase ha-tracking-wider">
                    État des plateformes
                  </h4>
                  <div className="ha-grid ha-grid-cols-2 ha-gap-3">
                    {selectedOTAs.map((ota) => {
                      const isFound = foundResults.some((r) => r.otaId === ota);
                      const isCurrent = currentOTA === ota && !isFound;

                      return (
                        <div
                          key={ota}
                          className={`ha-relative ha-p-4 ha-rounded-xl ha-border-2 ha-transition-all ha-duration-500 ${
                            isFound
                              ? 'ha-border-primary ha-bg-primary/10'
                              : isCurrent
                              ? 'ha-border-primary ha-bg-primary/10 ha-scale-105'
                              : 'ha-border-gray-700/50 ha-bg-gray-800/30 ha-opacity-50'
                          }`}
                          style={
                            isFound
                              ? { boxShadow: '0 0 20px rgba(30, 214, 179, 0.4)' }
                              : isCurrent
                              ? { boxShadow: '0 0 20px rgba(30, 214, 179, 0.4)' }
                              : {}
                          }
                        >
                          <div className="ha-flex ha-items-center ha-gap-3">
                            {isFound ? (
                              <CheckCircle className="ha-w-5 ha-h-5 ha-text-primary" />
                            ) : isCurrent ? (
                              <Loader2 className="ha-w-5 ha-h-5 ha-text-primary ha-animate-spin" />
                            ) : (
                              <div className="ha-w-5 ha-h-5 ha-rounded-full ha-border-2 ha-border-gray-600" />
                            )}
                            <span className={`ha-text-sm ha-font-semibold ${
                              isFound ? 'ha-text-primary' : 
                              isCurrent ? 'ha-text-primary' : 
                              'ha-text-gray-500'
                            }`}>
                              {OTA_NAMES[ota]}
                            </span>
                          </div>
                          {isFound && (
                            <div className="ha-absolute ha-top-2 ha-right-2 ha-w-2 ha-h-2 ha-bg-primary ha-rounded-full ha-animate-pulse" 
                                 style={{ boxShadow: '0 0 8px rgba(30, 214, 179, 1)' }} />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Info Cards */}
                <div className="ha-grid ha-grid-cols-2 ha-gap-4 ha-mt-6">
                  <div className="ha-p-4 ha-rounded-xl ha-border ha-border-primary/30 ha-bg-gray-800/40 ha-backdrop-blur-sm">
                    <div className="ha-text-3xl ha-mb-2">⚡</div>
                    <div className="ha-text-sm ha-font-bold ha-text-white">Analyse Temps Réel</div>
                    <div className="ha-text-xs ha-text-gray-300">Données actualisées</div>
                  </div>
                  <div className="ha-p-4 ha-rounded-xl ha-border ha-border-primary/30 ha-bg-gray-800/40 ha-backdrop-blur-sm">
                    <div className="ha-text-3xl ha-mb-2">🔒</div>
                    <div className="ha-text-sm ha-font-bold ha-text-white">100% Sécurisé</div>
                    <div className="ha-text-xs ha-text-gray-300">Données cryptées</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes slideInLeft {
          from {
            opacity: 0;
            transform: translateX(-50px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
      `}</style>
    </section>
  );
};